import React, { useState, useEffect, useCallback } from 'react';
import { getSalesSummaryReport, createSalesSummaryReportDownload } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import ReportHeader from '../ReportHeader/ReportHeader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './SalesSummaryReport.module.css';
import { calculateReportDateRange, getPeriodText, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { attachSharedUserChannelListener } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';
import logger from '../../../../Utils/logger';

const isMissing = (value) => value === null || value === undefined || value === '';

const formatCurrency = (value) => {
    if (isMissing(value)) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return `$ ${num.toFixed(2)}`;
};

const formatNumber = (value) => {
    if (isMissing(value)) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

const formatPercent = (value) => {
    if (isMissing(value)) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return `${num.toFixed(2)}%`;
};

const Row = ({ label, value, bold = false, formatter = formatCurrency, className = '' }) => (
    <div className={`${styles.row} ${bold ? styles.rowBold : ''} ${className}`.trim()}>
        <span className={styles.rowLabel}>{label}</span>
        <span className={styles.rowValue}>{formatter(value)}</span>
    </div>
);

const SectionHeader = ({ title, showDollar = true }) => (
    <div className={styles.sectionHeader}>
        <span>{title}</span>
        {showDollar ? <span>$</span> : null}
    </div>
);

const PaymentGroup = ({ items, totalLabel, total }) => (
    <div className={styles.paymentGroup}>
        {(Array.isArray(items) ? items : []).map((item, index) => (
            <Row
                key={`${item?.method || 'method'}-${index}`}
                label={item?.method || '-'}
                value={item?.amount}
            />
        ))}
        <Row label={totalLabel} value={total} bold />
    </div>
);

const SalesSummaryReport = ({ filterParams }) => {
    const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [outletName, setOutletName] = useState('');
    const [periodText, setPeriodText] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);

    useEffect(() => {
        if (selectedOutlet && userAssignedOutlets?.length) {
            const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
            setOutletName(outlet?.name || 'Unknown');
        }
    }, [selectedOutlet, userAssignedOutlets]);

    const hasReportData = Boolean(
        reportData?.sales_table ||
        reportData?.net_sales_report ||
        reportData?.covers ||
        reportData?.total_collection ||
        reportData?.category_breakdown ||
        reportData?.therapist_performance ||
        reportData?.room_utilization
    );

    const fetchSummaryReport = useCallback(async () => {
        try {
            setLoading(true);

            if (!filterParams) {
                setReportData(null);
                return;
            }

            const { startDate, endDate } = calculateReportDateRange(filterParams);

            const params = {
                start_date: startDate,
                end_date: endDate,
                outlet_id: filterParams.outlet || selectedOutlet
            };

            setPeriodText(getPeriodText(filterParams));

            const response = await getSalesSummaryReport(params);

            const isSuccess = response?.data?.success ?? response?.success ?? false;
            if (isSuccess) {
                const payload = response?.data?.data || null;
                setReportData(payload);

                if (Array.isArray(payload?.outlets) && payload.outlets.length > 0) {
                    setOutletName(payload.outlets.join(', '));
                }
            } else {
                toast.error(
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    response?.message ||
                    'Failed to fetch sales summary report'
                );
                setReportData(null);
            }
        } catch (error) {
            console.error('Error fetching sales summary report:', error);
            toast.error('An error occurred while fetching sales summary report');
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, [filterParams, selectedOutlet]);

    useEffect(() => {
        if (filterParams && filterParams.date && filterParams.outlet === selectedOutlet) {
            setReportData(null);
            fetchSummaryReport();
        }
    }, [filterParams, fetchSummaryReport, selectedOutlet]);

    const handleDownloadCSV = async () => {
        if (!hasReportData) {
            toast.warning('No data available to download');
            return;
        }
        if (!isReportRangeWithinMonths(filterParams, 3)) {
            toast.error('Please select a date range within 90 days to proceed.');
            return;
        }

        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');

        if (!userId) {
            toast.error('User ID not found');
            return;
        }

        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        const { startDate, endDate } = calculateReportDateRange(filterParams);
        const channelName = `user.${userId}`;
        let channel;
        let toastId = null;

        setDownloadLoading(true);
        toastId = toast.loading('Downloading in progress...');

        try {
            const eventName = 'SalesSummaryV2ReportReady';
            let detach = null;

            const cleanup = () => {
                try {
                    detach?.();
                } catch (e) {}
                if (toastId) {
                    toast.dismiss(toastId);
                    toastId = null;
                }
            };

            const { channel: joinedChannel, detach: detachListener } = attachSharedUserChannelListener(
                channelName,
                eventName,
                async (event) => {
                    try {
                        const downloadUrl = event?.url || event?.data?.url;

                        if (!downloadUrl) {
                            toast.error('Download URL not received. Please try again.');
                            cleanup();
                            setDownloadLoading(false);
                            return;
                        }

                        try {
                            await downloadFileWithAuth(
                                downloadUrl,
                                getDownloadFilename(event, downloadUrl, 'sales-summary.csv')
                            );
                            toast.success('Downloaded successfully');
                        } catch (downloadError) {
                            logger.error('Sales summary download failed:', downloadError);
                            toast.error('Failed to start download');
                        }

                        cleanup();
                        setDownloadLoading(false);
                    } catch (error) {
                        toast.error('Failed to process download');
                        cleanup();
                        setDownloadLoading(false);
                    }
                }
            );
            channel = joinedChannel;
            detach = detachListener;

            if (typeof channel.error === 'function') {
                channel.error(() => {
                    toast.error('Socket connection error');
                    cleanup();
                    setDownloadLoading(false);
                });
            }

            await new Promise((resolve) => setTimeout(resolve, 500));

            const downloadParams = {
                start_date: startDate,
                end_date: endDate,
                outlet_id: filterParams.outlet || selectedOutlet,
                user_id: userId
            };

            const createResponse = await createSalesSummaryReportDownload(downloadParams);

            const responseMessage =
                createResponse?.data?.data?.message ||
                createResponse?.data?.message ||
                createResponse?.message ||
                '';

            const responseStatus =
                createResponse?.data?.data?.status ||
                createResponse?.data?.status ||
                createResponse?.status;

            const isSuccess =
                createResponse?.data?.data?.success ||
                createResponse?.data?.success ||
                createResponse?.success ||
                responseStatus === 'processing' ||
                responseStatus === 'success';

            const isGeneratingMessage =
                typeof responseMessage === 'string' &&
                (responseMessage.toLowerCase().includes('is being generated') ||
                    responseMessage.toLowerCase().includes('you will be notified'));

            if (isSuccess || isGeneratingMessage) {
                return;
            }

            if (!isSuccess) {
                const backendError = responseMessage || 'Failed to initiate download';
                toast.error(backendError);
                cleanup();
                setDownloadLoading(false);
            }
        } catch (error) {
            console.error('Error downloading sales summary report:', error);
            toast.error(error?.message || 'An error occurred while exporting sales summary report');
            setDownloadLoading(false);
        }
    };

    const salesTable = reportData?.sales_table;
    const netSalesReport = reportData?.net_sales_report;
    const covers = reportData?.covers;
    const totalCollection = reportData?.total_collection;
    const categoryBreakdown = reportData?.category_breakdown;
    const therapist = reportData?.therapist_performance;
    const room = reportData?.room_utilization;

    return (
        <div className={styles.salesReportContainer}>
            <ReportHeader
                outletName={outletName}
                periodText={periodText}
                onDownload={handleDownloadCSV}
                downloadLoading={downloadLoading}
                hasData={hasReportData}
                downloadButtonText="Download PDF"
            />

            {loading ? (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            ) : hasReportData ? (
                <div className={styles.reportGrid}>
                    <div className={styles.leftColumn}>
                        {/* Sales */}
                        <div className={styles.card}>
                            <SectionHeader title="Sales" />
                            <div className={`${styles.toneBlock} ${styles.toneService}`}>
                                <Row label="Service Sales" value={salesTable?.service?.gross} bold />
                                <Row label="Service VC Redemption" value={salesTable?.service?.vc_redemption} />
                                <Row label="Discount + FOC" value={salesTable?.service?.discount_foc} />
                                <Row label="Refund" value={salesTable?.service?.refund} />
                                <Row label="Net Service Sales" value={salesTable?.service?.net} bold />
                            </div>

                            <div className={`${styles.toneBlock} ${styles.toneVoucher}`}>
                                <Row label="Cash Voucher Sales" value={salesTable?.cash_voucher?.gross} bold />
                                <Row label="Discount + FOC" value={salesTable?.cash_voucher?.discount_foc} />
                                <Row label="Refund" value={salesTable?.cash_voucher?.refund} />
                                <Row label="Net Cash Voucher Sales" value={salesTable?.cash_voucher?.net} bold />
                            </div>

                            <div className={`${styles.toneBlock} ${styles.toneProduct}`}>
                                <Row label="Product Sales" value={salesTable?.product?.gross} bold />
                                <Row label="Discount + FOC" value={salesTable?.product?.discount_foc} />
                                <Row label="Refund" value={salesTable?.product?.refund} />
                                <Row label="Net Product Sales" value={salesTable?.product?.net} bold />
                            </div>

                            <div className={styles.toneBlock}>
                                <Row label="Total Sales" value={salesTable?.totals?.total_sales} bold />
                                <Row label="GST @9%" value={salesTable?.totals?.gst} />
                                <Row label="Rnd Adjustment" value={salesTable?.totals?.rnd_adjustment} />
                            </div>

                            <div className={styles.toneBlock}>
                                <div className={styles.blockTitle}>Additional Revenue</div>
                                <Row
                                    label="- Excess from cash voucher"
                                    value={salesTable?.additional_revenue?.excess_from_cash_voucher}
                                />
                                <Row
                                    label="Member Credit Top Up"
                                    value={salesTable?.additional_revenue?.member_credit_top_up}
                                />
                            </div>

                            <div className={`${styles.toneBlock} ${styles.totalCollectionsBlock}`}>
                                <Row label="Total Collections" value={salesTable?.total_collections} bold />
                                <p className={styles.subLabel}>(Sales + GST + Top up + rounding + tips)</p>
                            </div>
                        </div>

                        {/* Net Sales */}
                        <div className={styles.card}>
                            <SectionHeader title="Net Sales (Outlet daily sales report)" />
                            <div className={styles.cardBody}>
                                <Row
                                    label="Service Sales - Non- Member (Included VC Redemption)"
                                    value={netSalesReport?.service_non_member}
                                />
                                <Row label="Service Sales - Member" value={netSalesReport?.service_member} />
                                <Row label="Other Sales" value={netSalesReport?.other_sales} />
                                <Row label="Net Sales" value={netSalesReport?.net_sales} bold />
                                <Row label="Member Top-Up" value={netSalesReport?.member_top_up} />
                                <Row label="Member Redemption" value={netSalesReport?.member_redemption} />
                                <Row label="Total Balance" value={netSalesReport?.total_balance} bold />
                            </div>
                        </div>

                        {/* Covers */}
                        <div className={styles.card}>
                            <SectionHeader title="Covers" />
                            <div className={styles.cardBody}>
                                <Row
                                    label="Number of Pax"
                                    value={covers?.number_of_pax}
                                    formatter={formatNumber}
                                />
                                <Row
                                    label="Average Spending per Pax"
                                    value={covers?.average_spending_per_pax}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightColumn}>
                        {/* Total Collection */}
                        <div className={styles.card}>
                            <SectionHeader title="Total Collection" />
                            <div className={styles.cardBody}>
                                <PaymentGroup
                                    items={totalCollection?.card?.items}
                                    totalLabel="Total Card"
                                    total={totalCollection?.card?.total}
                                />
                                <PaymentGroup
                                    items={totalCollection?.cash?.items}
                                    totalLabel="Total Cash"
                                    total={totalCollection?.cash?.total}
                                />
                                <PaymentGroup
                                    items={totalCollection?.voucher?.items}
                                    totalLabel="Total Voucher"
                                    total={totalCollection?.voucher?.total}
                                />
                                <PaymentGroup
                                    items={totalCollection?.member_redemption?.items}
                                    totalLabel="Total Member Redemption"
                                    total={totalCollection?.member_redemption?.total}
                                />
                                {(Array.isArray(totalCollection?.point_redemption?.items) &&
                                    totalCollection.point_redemption.items.length > 0) ? (
                                    <PaymentGroup
                                        items={totalCollection.point_redemption.items}
                                        totalLabel="Total Point Redemption"
                                        total={totalCollection?.point_redemption?.total}
                                    />
                                ) : (
                                    <div className={styles.paymentGroup}>
                                        <Row
                                            label="NL Point Redemption"
                                            value={totalCollection?.point_redemption?.total}
                                        />
                                        <Row
                                            label="Total Point Redemption"
                                            value={totalCollection?.point_redemption?.total}
                                            bold
                                        />
                                    </div>
                                )}
                                <Row
                                    label="Total Collection"
                                    value={totalCollection?.grand_total}
                                    bold
                                    className={styles.grandTotalRow}
                                />
                            </div>
                        </div>

                        {/* Category breakdown */}
                        <div className={styles.card}>
                            <div className={styles.cardBody}>
                                <Row
                                    label={categoryBreakdown?.body_sales?.label || 'Body Sales (Body, Tuina, slimming/ pre natal/ scr)'}
                                    value={categoryBreakdown?.body_sales?.amount}
                                />
                                <Row
                                    label={categoryBreakdown?.foot_sales?.label || 'Foot Sales (foot, 4in1, 5in1, head)'}
                                    value={categoryBreakdown?.foot_sales?.amount}
                                />
                                <Row label="Total Sales" value={categoryBreakdown?.total_sales} bold />
                            </div>
                        </div>

                        {/* Therapist performance */}
                        <div className={styles.card}>
                            <table className={styles.metricTable}>
                                <thead>
                                    <tr>
                                        <th className={styles.metricLabelCol}>Therapist</th>
                                        <th>F</th>
                                        <th>M</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Total Working Therapist (pax)</td>
                                        <td>{formatNumber(therapist?.working_therapists?.f)}</td>
                                        <td>{formatNumber(therapist?.working_therapists?.m)}</td>
                                        <td>{formatNumber(therapist?.working_therapists?.total)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Working Hour</td>
                                        <td>{formatNumber(therapist?.working_hours?.f)}</td>
                                        <td>{formatNumber(therapist?.working_hours?.m)}</td>
                                        <td>{formatNumber(therapist?.working_hours?.total)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Service Hour</td>
                                        <td>{formatNumber(therapist?.service_hours?.f)}</td>
                                        <td>{formatNumber(therapist?.service_hours?.m)}</td>
                                        <td>{formatNumber(therapist?.service_hours?.total)}</td>
                                    </tr>
                                    <tr>
                                        <td>% of Total Service Hrs / Working Hrs</td>
                                        <td>{formatPercent(therapist?.utilization_percent?.f)}</td>
                                        <td>{formatPercent(therapist?.utilization_percent?.m)}</td>
                                        <td>{formatPercent(therapist?.utilization_percent?.total)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className={styles.metricFooter}>
                                <span>Average Sales per therapist = Net sales / Therapist</span>
                                <span>{formatCurrency(therapist?.average_sales_per_therapist)}</span>
                            </div>
                        </div>

                        {/* Room utilization */}
                        <div className={styles.card}>
                            <table className={styles.metricTable}>
                                <thead>
                                    <tr>
                                        <th className={styles.metricLabelCol}>Room</th>
                                        <th>B</th>
                                        <th>F</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Total Available Room</td>
                                        <td>{formatNumber(room?.available_rooms?.b)}</td>
                                        <td>{formatNumber(room?.available_rooms?.f)}</td>
                                    </tr>
                                    <tr>
                                        <td>Room Available Hour</td>
                                        <td>{formatNumber(room?.available_hours?.b)}</td>
                                        <td>{formatNumber(room?.available_hours?.f)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Service Hour</td>
                                        <td>{formatNumber(room?.service_hours?.b)}</td>
                                        <td>{formatNumber(room?.service_hours?.f)}</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>% on Room Utilisation</div>
                                            <div className={styles.cellSubLabel}>
                                                (% of Total Service hrs / Available hrs)
                                            </div>
                                        </td>
                                        <td>{formatPercent(room?.utilization_percent?.b)}</td>
                                        <td>{formatPercent(room?.utilization_percent?.f)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className={`${styles.metricFooter} ${styles.metricFooterDark}`}>
                                <span>Average Sales per room = Net sales / Room</span>
                                <div className={styles.roomAvgValues}>
                                    <span>{formatCurrency(room?.average_sales_per_room?.b)}</span>
                                    <span>{formatCurrency(room?.average_sales_per_room?.f)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white" />}
                        heading="No sales summary data found"
                        para="Please try changing the filters"
                    />
                </div>
            )}
        </div>
    );
};

export default SalesSummaryReport;
